import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiMenu, FiGrid, FiBook, FiLock } from 'react-icons/fi';
import Layout from '../../Layout/Layout';
import styles from './DNSRecords.module.css';
import { URL as BASEURL } from '../../AxiosUrlGlobal';

/**
 * DNSRecords - DNS Records Management
 * Complete CRUD operations for managing DNS records
 */
function DNSRecords() {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [activeTab, setActiveTab] = useState('basic');
    const [submitting, setSubmitting] = useState(false);
    const [users, setUsers] = useState([]);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
    const [poolIps, setPoolIps] = useState([]); // pool IPs available to skip per domain

    const [formData, setFormData] = useState({
        domain: '',
        uid: '',
        isLocal: true,
        isBangladesh: false,
        isActive: true,
        excludedIps: [],
        records: {
            A: { enabled: true, ttl: 300, values: [] },
            AAAA: { enabled: false, ttl: 300, values: [] },
            SOA: { enabled: true, ttl: 3600, mname: '', rname: '' },
            NS: { enabled: true, ttl: 3600, values: [] },
            MX: { enabled: false, ttl: 3600, values: [] },
            TXT: { enabled: false, ttl: 300, values: [] },
            SPF: { enabled: false, ttl: 3600, value: '' },
            DKIM: { enabled: false, ttl: 3600, values: [] },
            DMARC: { enabled: false, ttl: 3600, value: '' },
            ACME: { enabled: false, ttl: 300, value: '' },
            CNAME: { enabled: false, ttl: 300, target: '' },
            CAA: { enabled: false, ttl: 3600, values: [] },
            PTR: { enabled: false, ttl: 3600, target: '' },
            SRV: { enabled: false, ttl: 3600, values: [] },
            DNSKEY: { enabled: false, ttl: 86400, flags: 257, protocol: 3, algorithm: 8, publicKey: '' }
        }
    });

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        limit: 10,
        hasPrevPage: false,
        hasNextPage: false
    });

    const [filters, setFilters] = useState({
        domain: '',
        isActive: 'all',
        uid: ''
    });

    const userId = localStorage.getItem('id');
    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchRecords();
    }, [pagination.currentPage, filters]);

    useEffect(() => {
        fetchUsers();
        fetchPoolIps();
    }, []);

    // Pool IPs come from the active DNS config — the same list the DNS server
    // rotates between. Used to build the per-domain "skip IP" checkboxes.
    const fetchPoolIps = async () => {
        try {
            const response = await axios.get(
                `${BASEURL}superadmin/dns-configs/${userId}?page=1&limit=10`,
                { headers: { token } }
            );

            if (!response.data.success) return;

            const config = (response.data.data.configs || []).find(c => c.isActive)
                || (response.data.data.configs || [])[0];
            if (!config) return;

            const collect = (pool, poolName) => (pool || []).map(entry => ({
                ip: entry.ip,
                label: entry.label || '',
                pool: poolName
            }));

            // Dedupe by IP — an IP sitting in both pools is still one checkbox
            const merged = [];
            const seen = new Set();
            [...collect(config.ipPool, 'Global'), ...collect(config.bangladeshIpPool, 'Bangladesh')]
                .forEach(entry => {
                    if (!entry.ip || seen.has(entry.ip)) return;
                    seen.add(entry.ip);
                    merged.push(entry);
                });

            setPoolIps(merged);
        } catch (err) {
            console.error('Error fetching DNS pool IPs:', err);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get(
                `${BASEURL}admin-user/${userId}?page=1&limit=1000`,
                { headers: { token } }
            );

            if (response.data.success) {
                setUsers(response.data.data.users || []);
            }
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    };

    const fetchRecords = async () => {
        try {
            setLoading(true);
            setError('');

            const queryParams = new URLSearchParams({
                page: pagination.currentPage,
                limit: pagination.limit,
                ...(filters.isActive !== 'all' && { isActive: filters.isActive }),
                ...(filters.domain && { domain: filters.domain }),
                ...(filters.uid && { uid: filters.uid })
            });

            const response = await axios.get(
                `${BASEURL}superadmin/dns-records/${userId}?${queryParams}`,
                { headers: { token } }
            );

            if (response.data.success) {
                setRecords(response.data.data.records);
                setPagination(response.data.data.pagination);
            } else {
                setError(response.data.error || 'Failed to load DNS records');
            }
        } catch (err) {
            console.error('Error fetching DNS records:', err);
            setError(err.response?.data?.error || 'Failed to load DNS records');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (record = null) => {
        // Default records structure
        const defaultRecords = {
            A: { enabled: true, ttl: 300, values: [] },
            AAAA: { enabled: false, ttl: 300, values: [] },
            SOA: { enabled: true, ttl: 3600, mname: '', rname: '' },
            NS: { enabled: true, ttl: 3600, values: [] },
            MX: { enabled: false, ttl: 3600, values: [] },
            TXT: { enabled: false, ttl: 300, values: [] },
            SPF: { enabled: false, ttl: 3600, value: '' },
            DKIM: { enabled: false, ttl: 3600, values: [] },
            DMARC: { enabled: false, ttl: 3600, value: '' },
            ACME: { enabled: false, ttl: 300, value: '' },
            CNAME: { enabled: false, ttl: 300, target: '' },
            CAA: { enabled: false, ttl: 3600, values: [] },
            PTR: { enabled: false, ttl: 3600, target: '' },
            SRV: { enabled: false, ttl: 3600, values: [] },
            DNSKEY: { enabled: false, ttl: 86400, flags: 257, protocol: 3, algorithm: 8, publicKey: '' }
        };

        if (record) {
            setEditingRecord(record);
            // Merge backend data with defaults to ensure all record types exist
            const mergedRecords = { ...defaultRecords };
            if (record.records) {
                Object.keys(record.records).forEach(key => {
                    if (mergedRecords[key]) {
                        mergedRecords[key] = { ...mergedRecords[key], ...record.records[key] };
                    }
                });
            }
            // Backward compat: migrate legacy single DKIM to array format
            if (mergedRecords.DKIM && typeof mergedRecords.DKIM.selector === 'string') {
                const legacyEntries = mergedRecords.DKIM.value
                    ? [{ selector: mergedRecords.DKIM.selector, value: mergedRecords.DKIM.value }]
                    : [];
                mergedRecords.DKIM.values = legacyEntries;
                delete mergedRecords.DKIM.selector;
                delete mergedRecords.DKIM.value;
            }
            setFormData({
                domain: record.domain,
                uid: record.uid,
                isLocal: record.isLocal,
                isBangladesh: record.isBangladesh || false,
                isActive: record.isActive,
                excludedIps: record.excludedIps || [],
                records: mergedRecords
            });
        } else {
            setEditingRecord(null);
            setFormData({
                domain: '',
                uid: '',
                isLocal: true,
                isBangladesh: false,
                isActive: true,
                excludedIps: [],
                records: defaultRecords
            });
        }
        setActiveTab('basic');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingRecord(null);
        setActiveTab('basic');
    };

    const handleSubmit = async () => {
        try {
            // Validate required fields
            if (!formData.domain || !formData.domain.trim()) {
                alert('Domain name is required');
                return;
            }

            if (!formData.uid || !formData.uid.trim()) {
                alert('Store Owner is required. Please select a store owner from the dropdown.');
                return;
            }

            setSubmitting(true);

            const url = editingRecord
                ? `${BASEURL}superadmin/dns-record/${userId}/${editingRecord._id}`
                : `${BASEURL}superadmin/dns-record/${userId}`;

            const method = editingRecord ? 'put' : 'post';

            const response = await axios[method](url, formData, { headers: { token } });

            if (response.data.success) {
                closeModal();
                fetchRecords();
                alert('DNS record saved successfully!');
            } else {
                alert(response.data.error || 'Failed to save DNS record');
            }
        } catch (err) {
            console.error('Error saving DNS record:', err);
            alert(err.response?.data?.error || 'Failed to save DNS record');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (recordId, domain) => {
        if (!window.confirm(`Are you sure you want to delete DNS record for "${domain}"?`)) {
            return;
        }

        try {
            const response = await axios.delete(
                `${BASEURL}superadmin/dns-record/${userId}/${recordId}`,
                { headers: { token } }
            );

            if (response.data.success) {
                fetchRecords();
            } else {
                alert(response.data.error || 'Failed to delete DNS record');
            }
        } catch (err) {
            console.error('Error deleting DNS record:', err);
            alert(err.response?.data?.error || 'Failed to delete DNS record');
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const getRecordTypeCounts = (records) => {
        const counts = {};
        if (records.A?.enabled && records.A.values?.length > 0) counts.A = records.A.values.length;
        if (records.AAAA?.enabled && records.AAAA.values?.length > 0) counts.AAAA = records.AAAA.values.length;
        if (records.MX?.enabled && records.MX.values?.length > 0) counts.MX = records.MX.values.length;
        if (records.NS?.enabled && records.NS.values?.length > 0) counts.NS = records.NS.values.length;
        if (records.TXT?.enabled && records.TXT.values?.length > 0) counts.TXT = records.TXT.values.length;
        if (records.CAA?.enabled && records.CAA.values?.length > 0) counts.CAA = records.CAA.values.length;
        if (records.CNAME?.enabled) counts.CNAME = 1;
        if (records.SOA?.enabled) counts.SOA = 1;
        if (records.ACME?.enabled) counts.ACME = 1;
        return counts;
    };

    // Skip / un-skip a pool IP for this domain
    const toggleExcludedIp = (ip) => {
        setFormData(prev => {
            const current = prev.excludedIps || [];
            return {
                ...prev,
                excludedIps: current.includes(ip)
                    ? current.filter(existing => existing !== ip)
                    : [...current, ip]
            };
        });
    };

    // Array field helpers
    const addArrayItem = (recordType, item) => {
        setFormData(prev => ({
            ...prev,
            records: {
                ...prev.records,
                [recordType]: {
                    ...prev.records[recordType],
                    values: [...(prev.records[recordType].values || []), item]
                }
            }
        }));
    };

    const removeArrayItem = (recordType, index) => {
        setFormData(prev => ({
            ...prev,
            records: {
                ...prev.records,
                [recordType]: {
                    ...prev.records[recordType],
                    values: prev.records[recordType].values.filter((_, i) => i !== index)
                }
            }
        }));
    };

    const updateArrayItem = (recordType, index, field, value) => {
        setFormData(prev => ({
            ...prev,
            records: {
                ...prev.records,
                [recordType]: {
                    ...prev.records[recordType],
                    values: prev.records[recordType].values.map((item, i) =>
                        i === index ? { ...item, [field]: value } : item
                    )
                }
            }
        }));
    };

    // Order Store Owner options so the Super Admin shows first:
    // the logged-in admin at the very top, then any other admins (role === 1),
    // then the remaining users.
    const sortedUsers = [...users].sort((a, b) => {
        if (a._id === userId) return -1;
        if (b._id === userId) return 1;
        const aAdmin = a.role === 1 ? 0 : 1;
        const bAdmin = b.role === 1 ? 0 : 1;
        return aAdmin - bAdmin;
    });

    const renderBasicTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>Domain *</label>
                <input
                    type="text"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    placeholder="example.com"
                    disabled={!!editingRecord}
                />
            </div>

            <div className={styles.formGroup}>
                <label>Store Owner *</label>
                <select
                    value={formData.uid}
                    onChange={(e) => setFormData({ ...formData, uid: e.target.value })}
                    required
                >
                    <option value="">Select Store Owner</option>
                    {sortedUsers.map((user) => {
                        const label = user.username || user.mobile || user._id;
                        const tag = user._id === userId
                            ? ' (Super Admin — You)'
                            : user.role === 1
                                ? ' (Super Admin)'
                                : '';
                        return (
                            <option key={user._id} value={user._id}>
                                {label} - {user.mobile}{tag}
                            </option>
                        );
                    })}
                </select>
            </div>

            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.isLocal}
                        onChange={(e) => setFormData({
                            ...formData,
                            isLocal: e.target.checked,
                            isBangladesh: e.target.checked ? formData.isBangladesh : false,
                            excludedIps: e.target.checked ? formData.excludedIps : []
                        })}
                    />
                    Local Domain (Uses IP Pool)
                </label>
            </div>

            {formData.isLocal && (
                <div className={styles.formGroup}>
                    <label>
                        <input
                            type="checkbox"
                            checked={formData.isBangladesh || false}
                            onChange={(e) => setFormData({ ...formData, isBangladesh: e.target.checked })}
                        />
                        Bangladesh (Use BD IP Pool)
                    </label>
                </div>
            )}

            {formData.isLocal && poolIps.length > 0 && (
                <div className={styles.formGroup}>
                    <label>Skip Pool IPs for this Domain</label>
                    <div className={styles.ipSkipList}>
                        {poolIps.map((entry) => {
                            const skipped = (formData.excludedIps || []).includes(entry.ip);
                            return (
                                <label
                                    key={entry.ip}
                                    className={`${styles.ipSkipItem} ${skipped ? styles.ipSkipItemActive : ''}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={skipped}
                                        onChange={() => toggleExcludedIp(entry.ip)}
                                    />
                                    <span className={styles.ipSkipAddress}>{entry.ip}</span>
                                    <span className={styles.ipSkipMeta}>
                                        {entry.label ? `${entry.label} • ` : ''}{entry.pool} Pool
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                    <p className={styles.ipSkipHint}>
                        Checked IPs are never returned for <strong>{formData.domain || 'this domain'}</strong>.
                        The rest of the pool keeps rotating and failing over normally. Applies to this
                        record only — every other domain and subdomain still uses the full pool.
                    </p>
                </div>
            )}

            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    Active
                </label>
            </div>
        </div>
    );

    const renderARecordsTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.A.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, A: { ...formData.records.A, enabled: e.target.checked } }
                        })}
                    />
                    Enable A Records
                </label>
            </div>

            <div className={styles.formGroup}>
                <label>TTL (seconds)</label>
                <input
                    type="number"
                    value={formData.records.A.ttl}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: { ...formData.records, A: { ...formData.records.A, ttl: parseInt(e.target.value) || 300 } }
                    })}
                />
            </div>

            <div className={styles.arrayHeader}>
                <h3>IPv4 Addresses</h3>
                <button
                    type="button"
                    className={styles.addArrayButton}
                    onClick={() => addArrayItem('A', '')}
                >
                    + Add IP
                </button>
            </div>

            {formData.records.A.values.map((ip, index) => (
                <div key={index} className={styles.arrayItem}>
                    <input
                        type="text"
                        value={ip}
                        onChange={(e) => {
                            const newValues = [...formData.records.A.values];
                            newValues[index] = e.target.value;
                            setFormData({
                                ...formData,
                                records: { ...formData.records, A: { ...formData.records.A, values: newValues } }
                            });
                        }}
                        placeholder="192.168.1.1"
                    />
                    <button
                        type="button"
                        className={styles.removeArrayButton}
                        onClick={() => removeArrayItem('A', index)}
                    >
                        Remove
                    </button>
                </div>
            ))}
        </div>
    );

    const renderNSRecordsTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.NS.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, NS: { ...formData.records.NS, enabled: e.target.checked } }
                        })}
                    />
                    Enable NS Records
                </label>
            </div>

            <div className={styles.formGroup}>
                <label>TTL (seconds)</label>
                <input
                    type="number"
                    value={formData.records.NS.ttl}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: { ...formData.records, NS: { ...formData.records.NS, ttl: parseInt(e.target.value) || 3600 } }
                    })}
                />
            </div>

            <div className={styles.arrayHeader}>
                <h3>Name Servers</h3>
                <button
                    type="button"
                    className={styles.addArrayButton}
                    onClick={() => addArrayItem('NS', '')}
                >
                    + Add NS
                </button>
            </div>

            {formData.records.NS.values.map((ns, index) => (
                <div key={index} className={styles.arrayItem}>
                    <input
                        type="text"
                        value={ns}
                        onChange={(e) => {
                            const newValues = [...formData.records.NS.values];
                            newValues[index] = e.target.value;
                            setFormData({
                                ...formData,
                                records: { ...formData.records, NS: { ...formData.records.NS, values: newValues } }
                            });
                        }}
                        placeholder="ns1.example.com"
                    />
                    <button
                        type="button"
                        className={styles.removeArrayButton}
                        onClick={() => removeArrayItem('NS', index)}
                    >
                        Remove
                    </button>
                </div>
            ))}
        </div>
    );

    const renderMXRecordsTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.MX.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, MX: { ...formData.records.MX, enabled: e.target.checked } }
                        })}
                    />
                    Enable MX Records
                </label>
            </div>

            <div className={styles.formGroup}>
                <label>TTL (seconds)</label>
                <input
                    type="number"
                    value={formData.records.MX.ttl}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: { ...formData.records, MX: { ...formData.records.MX, ttl: parseInt(e.target.value) || 3600 } }
                    })}
                />
            </div>

            <div className={styles.arrayHeader}>
                <h3>Mail Servers</h3>
                <button
                    type="button"
                    className={styles.addArrayButton}
                    onClick={() => addArrayItem('MX', { priority: 10, exchange: '' })}
                >
                    + Add MX
                </button>
            </div>

            {formData.records.MX.values.map((mx, index) => (
                <div key={index} className={styles.arrayItem}>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>Priority</label>
                            <input
                                type="number"
                                value={mx.priority || 10}
                                onChange={(e) => updateArrayItem('MX', index, 'priority', parseInt(e.target.value) || 10)}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Mail Server</label>
                            <input
                                type="text"
                                value={mx.exchange || ''}
                                onChange={(e) => updateArrayItem('MX', index, 'exchange', e.target.value)}
                                placeholder="mail.example.com"
                            />
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.removeArrayButton}
                        onClick={() => removeArrayItem('MX', index)}
                    >
                        Remove
                    </button>
                </div>
            ))}
        </div>
    );

    const renderTXTRecordsTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.TXT.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, TXT: { ...formData.records.TXT, enabled: e.target.checked } }
                        })}
                    />
                    Enable TXT Records
                </label>
            </div>

            <div className={styles.formGroup}>
                <label>TTL (seconds)</label>
                <input
                    type="number"
                    value={formData.records.TXT.ttl}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: { ...formData.records, TXT: { ...formData.records.TXT, ttl: parseInt(e.target.value) || 300 } }
                    })}
                />
            </div>

            <div className={styles.arrayHeader}>
                <h3>TXT Records</h3>
                <button
                    type="button"
                    className={styles.addArrayButton}
                    onClick={() => addArrayItem('TXT', '')}
                >
                    + Add TXT
                </button>
            </div>

            {formData.records.TXT.values.map((txt, index) => (
                <div key={index} className={styles.arrayItem}>
                    <textarea
                        value={txt}
                        onChange={(e) => {
                            const newValues = [...formData.records.TXT.values];
                            newValues[index] = e.target.value;
                            setFormData({
                                ...formData,
                                records: { ...formData.records, TXT: { ...formData.records.TXT, values: newValues } }
                            });
                        }}
                        placeholder="v=spf1 include:_spf.example.com ~all"
                        rows="3"
                    />
                    <button
                        type="button"
                        className={styles.removeArrayButton}
                        onClick={() => removeArrayItem('TXT', index)}
                    >
                        Remove
                    </button>
                </div>
            ))}

            <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e1e3e5' }} />

            {/* SPF */}
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.SPF.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, SPF: { ...formData.records.SPF, enabled: e.target.checked } }
                        })}
                    />
                    Enable SPF Record
                </label>
            </div>
            <div className={styles.formGroup}>
                <label>SPF Value</label>
                <input
                    type="text"
                    value={formData.records.SPF.value}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: { ...formData.records, SPF: { ...formData.records.SPF, value: e.target.value } }
                    })}
                    placeholder="v=spf1 include:_spf.example.com ~all"
                />
            </div>

            {/* DKIM */}
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.DKIM.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, DKIM: { ...formData.records.DKIM, enabled: e.target.checked } }
                        })}
                    />
                    Enable DKIM Record
                </label>
            </div>

            <div className={styles.arrayHeader}>
                <h3>DKIM Records</h3>
                <button
                    type="button"
                    className={styles.addArrayButton}
                    onClick={() => addArrayItem('DKIM', { selector: 'default', value: '' })}
                >
                    + Add DKIM
                </button>
            </div>

            {formData.records.DKIM.values.map((dkim, index) => (
                <div key={index} className={styles.arrayItem}>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>Selector</label>
                            <input
                                type="text"
                                value={dkim.selector}
                                onChange={(e) => {
                                    const newValues = [...formData.records.DKIM.values];
                                    newValues[index] = { ...newValues[index], selector: e.target.value };
                                    setFormData({
                                        ...formData,
                                        records: { ...formData.records, DKIM: { ...formData.records.DKIM, values: newValues } }
                                    });
                                }}
                                placeholder="default"
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Public Key</label>
                            <input
                                type="text"
                                value={dkim.value}
                                onChange={(e) => {
                                    const newValues = [...formData.records.DKIM.values];
                                    newValues[index] = { ...newValues[index], value: e.target.value };
                                    setFormData({
                                        ...formData,
                                        records: { ...formData.records, DKIM: { ...formData.records.DKIM, values: newValues } }
                                    });
                                }}
                                placeholder="v=DKIM1; k=rsa; p=..."
                            />
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.removeArrayButton}
                        onClick={() => removeArrayItem('DKIM', index)}
                    >
                        Remove
                    </button>
                </div>
            ))}

            {/* DMARC */}
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.DMARC.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, DMARC: { ...formData.records.DMARC, enabled: e.target.checked } }
                        })}
                    />
                    Enable DMARC Record
                </label>
            </div>
            <div className={styles.formGroup}>
                <label>DMARC Value</label>
                <input
                    type="text"
                    value={formData.records.DMARC.value}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: { ...formData.records, DMARC: { ...formData.records.DMARC, value: e.target.value } }
                    })}
                    placeholder="v=DMARC1; p=quarantine; rua=mailto:dmarc@example.com"
                />
            </div>
        </div>
    );

    const renderSSLTab = () => (
        <div className={styles.tabContent}>
            {/* ACME Challenge for SSL Certificate */}
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.ACME?.enabled || false}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: {
                                ...formData.records,
                                ACME: {
                                    ...formData.records.ACME,
                                    enabled: e.target.checked
                                }
                            }
                        })}
                    />
                    Enable ACME Challenge (SSL Certificate)
                </label>
                <p style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                    Auto-responds to _acme-challenge.{'{domain}'} DNS queries for SSL certificate validation (Let's Encrypt, certbot, acme.sh)
                </p>
            </div>

            <div className={styles.formGroup}>
                <label>ACME Challenge Token</label>
                <input
                    type="text"
                    value={formData.records.ACME?.value || ''}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: {
                            ...formData.records,
                            ACME: {
                                ...formData.records.ACME,
                                value: e.target.value
                            }
                        }
                    })}
                    placeholder="qaLGOuRin9gsjmRcUVqmGe9vt7bsuzG_GWdro71VPf0"
                    disabled={!formData.records.ACME?.enabled}
                />
                <p style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                    Paste the challenge token provided by certbot or your SSL certificate provider
                </p>
            </div>

            <div className={styles.formGroup}>
                <label>TTL (seconds)</label>
                <input
                    type="number"
                    value={formData.records.ACME?.ttl || 300}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: {
                            ...formData.records,
                            ACME: {
                                ...formData.records.ACME,
                                ttl: parseInt(e.target.value) || 300
                            }
                        }
                    })}
                    disabled={!formData.records.ACME?.enabled}
                />
                <p style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                    Recommended: 300 seconds (5 minutes) for faster validation
                </p>
            </div>

            {/* Example Usage */}
            <div style={{
                backgroundColor: '#f8f9fa',
                padding: '15px',
                borderRadius: '5px',
                marginTop: '20px',
                border: '1px solid #dee2e6'
            }}>
                <h4 style={{ marginTop: 0, marginBottom: '10px', color: '#495057' }}>
                    <FiBook style={{ verticalAlign: 'middle' }} /> How to Use ACME Challenge for SSL
                </h4>
                <ol style={{ marginBottom: 0, paddingLeft: '20px', color: '#6c757d', fontSize: '0.9em' }}>
                    <li>Run <code style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>certbot certonly --manual --preferred-challenges dns -d {formData.domain || 'yourdomain.com'}</code></li>
                    <li>Certbot will provide you an ACME challenge token</li>
                    <li>Enable ACME Challenge above and paste the token</li>
                    <li>Save this DNS record</li>
                    <li>Wait 1-2 minutes for DNS propagation</li>
                    <li>Press Enter in certbot to validate</li>
                    <li>Your SSL certificate will be issued!</li>
                </ol>
                <p style={{ marginTop: '10px', marginBottom: 0, fontSize: '0.85em', color: '#6c757d' }}>
                    <strong>Note:</strong> The ACME challenge automatically responds to queries for <code style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>_acme-challenge.{formData.domain || 'yourdomain.com'}</code>
                </p>
            </div>

            <hr style={{ margin: '32px 0', border: 'none', borderTop: '2px solid #dee2e6' }} />

            {/* CAA Records - Certificate Authority Authorization */}
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.CAA?.enabled || false}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: {
                                ...formData.records,
                                CAA: {
                                    ...formData.records.CAA,
                                    enabled: e.target.checked
                                }
                            }
                        })}
                    />
                    Enable CAA Records (Certificate Authority Authorization)
                </label>
                <p style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                    CAA records specify which certificate authorities (CAs) are allowed to issue SSL certificates for your domain. This adds an extra layer of security.
                </p>
            </div>

            <div className={styles.formGroup}>
                <label>TTL (seconds)</label>
                <input
                    type="number"
                    value={formData.records.CAA?.ttl || 3600}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: {
                            ...formData.records,
                            CAA: {
                                ...formData.records.CAA,
                                ttl: parseInt(e.target.value) || 3600
                            }
                        }
                    })}
                    disabled={!formData.records.CAA?.enabled}
                />
                <p style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                    Recommended: 3600 seconds (1 hour) - standard for CAA records
                </p>
            </div>

            <div className={styles.arrayHeader}>
                <h3>CAA Records</h3>
                <button
                    type="button"
                    className={styles.addArrayButton}
                    onClick={() => addArrayItem('CAA', { flags: 0, tag: 'issue', value: '' })}
                    disabled={!formData.records.CAA?.enabled}
                >
                    + Add CAA Record
                </button>
            </div>

            {formData.records.CAA?.values?.map((caa, index) => (
                <div key={index} className={styles.arrayItem}>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup} style={{ flex: '0 0 100px' }}>
                            <label>Flags</label>
                            <input
                                type="number"
                                value={caa.flags || 0}
                                onChange={(e) => updateArrayItem('CAA', index, 'flags', parseInt(e.target.value) || 0)}
                                placeholder="0"
                                disabled={!formData.records.CAA?.enabled}
                            />
                        </div>
                        <div className={styles.formGroup} style={{ flex: '0 0 150px' }}>
                            <label>Tag</label>
                            <select
                                value={caa.tag || 'issue'}
                                onChange={(e) => updateArrayItem('CAA', index, 'tag', e.target.value)}
                                disabled={!formData.records.CAA?.enabled}
                            >
                                <option value="issue">issue</option>
                                <option value="issuewild">issuewild</option>
                                <option value="iodef">iodef</option>
                            </select>
                        </div>
                        <div className={styles.formGroup} style={{ flex: 1 }}>
                            <label>Value (CA Domain or URL)</label>
                            <input
                                type="text"
                                value={caa.value || ''}
                                onChange={(e) => updateArrayItem('CAA', index, 'value', e.target.value)}
                                placeholder={caa.tag === 'iodef' ? 'mailto:admin@example.com' : 'letsencrypt.org'}
                                disabled={!formData.records.CAA?.enabled}
                            />
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.removeArrayButton}
                        onClick={() => removeArrayItem('CAA', index)}
                        disabled={!formData.records.CAA?.enabled}
                    >
                        Remove
                    </button>
                </div>
            ))}

            {/* CAA Information Box */}
            <div style={{
                backgroundColor: '#f8f9fa',
                padding: '15px',
                borderRadius: '5px',
                marginTop: '20px',
                border: '1px solid #dee2e6'
            }}>
                <h4 style={{ marginTop: 0, marginBottom: '10px', color: '#495057' }}>
                    <FiLock style={{ verticalAlign: 'middle' }} /> CAA Record Tags Explained
                </h4>
                <ul style={{ marginBottom: 0, paddingLeft: '20px', color: '#6c757d', fontSize: '0.9em', lineHeight: '1.6' }}>
                    <li><strong>issue</strong> - Authorizes the CA to issue standard SSL certificates for this domain</li>
                    <li><strong>issuewild</strong> - Authorizes the CA to issue wildcard SSL certificates (*.domain.com)</li>
                    <li><strong>iodef</strong> - Email or URL to report policy violations (e.g., mailto:security@example.com)</li>
                </ul>
                <p style={{ marginTop: '15px', marginBottom: '10px', fontSize: '0.9em', color: '#495057' }}>
                    <strong>Common Certificate Authorities:</strong>
                </p>
                <ul style={{ marginBottom: 0, paddingLeft: '20px', color: '#6c757d', fontSize: '0.85em', lineHeight: '1.5' }}>
                    <li><code style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>letsencrypt.org</code> - Let's Encrypt (Free, automated)</li>
                    <li><code style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>digicert.com</code> - DigiCert</li>
                    <li><code style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>sectigo.com</code> - Sectigo (formerly Comodo)</li>
                    <li><code style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>globalsign.com</code> - GlobalSign</li>
                    <li><code style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>; (semicolon)</code> - Disallow ALL certificate authorities</li>
                </ul>
                <p style={{ marginTop: '15px', marginBottom: 0, fontSize: '0.85em', color: '#6c757d' }}>
                    <strong>Example:</strong> To allow Let's Encrypt for both standard and wildcard certificates, add two records:<br />
                    • Tag: <code style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>issue</code>, Value: <code style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>letsencrypt.org</code><br />
                    • Tag: <code style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>issuewild</code>, Value: <code style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>letsencrypt.org</code>
                </p>
            </div>
        </div>
    );

    const renderOtherRecordsTab = () => (
        <div className={styles.tabContent}>
            {/* CNAME */}
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.CNAME.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, CNAME: { ...formData.records.CNAME, enabled: e.target.checked } }
                        })}
                    />
                    Enable CNAME Record
                </label>
            </div>
            <div className={styles.formGroup}>
                <label>CNAME Target</label>
                <input
                    type="text"
                    value={formData.records.CNAME.target}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: { ...formData.records, CNAME: { ...formData.records.CNAME, target: e.target.value } }
                    })}
                    placeholder="target.example.com"
                />
            </div>

            <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e1e3e5' }} />

            {/* SOA */}
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.SOA.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, SOA: { ...formData.records.SOA, enabled: e.target.checked } }
                        })}
                    />
                    Enable SOA Record
                </label>
            </div>
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>MNAME (Primary NS)</label>
                    <input
                        type="text"
                        value={formData.records.SOA.mname}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, SOA: { ...formData.records.SOA, mname: e.target.value } }
                        })}
                        placeholder="ns1.example.com"
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>RNAME (Admin Email)</label>
                    <input
                        type="text"
                        value={formData.records.SOA.rname}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, SOA: { ...formData.records.SOA, rname: e.target.value } }
                        })}
                        placeholder="admin.example.com"
                    />
                </div>
            </div>

            <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e1e3e5' }} />

            {/* AAAA */}
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.AAAA.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, AAAA: { ...formData.records.AAAA, enabled: e.target.checked } }
                        })}
                    />
                    Enable AAAA Records (IPv6)
                </label>
            </div>

            <div className={styles.arrayHeader}>
                <h3>IPv6 Addresses</h3>
                <button
                    type="button"
                    className={styles.addArrayButton}
                    onClick={() => addArrayItem('AAAA', '')}
                >
                    + Add IPv6
                </button>
            </div>

            {formData.records.AAAA.values.map((ip, index) => (
                <div key={index} className={styles.arrayItem}>
                    <input
                        type="text"
                        value={ip}
                        onChange={(e) => {
                            const newValues = [...formData.records.AAAA.values];
                            newValues[index] = e.target.value;
                            setFormData({
                                ...formData,
                                records: { ...formData.records, AAAA: { ...formData.records.AAAA, values: newValues } }
                            });
                        }}
                        placeholder="2001:0db8:85a3:0000:0000:8a2e:0370:7334"
                    />
                    <button
                        type="button"
                        className={styles.removeArrayButton}
                        onClick={() => removeArrayItem('AAAA', index)}
                    >
                        Remove
                    </button>
                </div>
            ))}
        </div>
    );

    return (
        <Layout>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div>
                        <h1>DNS Records Management</h1>
                        <p>View and manage DNS records for all domains in the system</p>
                    </div>
                    <div className={styles.headerActions}>
                        <div className={styles.viewToggle}>
                            <button
                                className={`${styles.viewButton} ${viewMode === 'list' ? styles.activeView : ''}`}
                                onClick={() => setViewMode('list')}
                                title="List View"
                            >
                                <FiMenu />
                            </button>
                            <button
                                className={`${styles.viewButton} ${viewMode === 'grid' ? styles.activeView : ''}`}
                                onClick={() => setViewMode('grid')}
                                title="Grid View"
                            >
                                <FiGrid />
                            </button>
                        </div>
                        <button className={styles.addButton} onClick={() => openModal()}>
                            + Add DNS Record
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className={styles.filters}>
                    <input
                        type="text"
                        placeholder="Search by domain..."
                        value={filters.domain}
                        onChange={(e) => handleFilterChange('domain', e.target.value)}
                        className={styles.searchInput}
                    />
                    <input
                        type="text"
                        placeholder="Filter by User ID..."
                        value={filters.uid}
                        onChange={(e) => handleFilterChange('uid', e.target.value)}
                        className={styles.searchInput}
                    />
                    <select
                        value={filters.isActive}
                        onChange={(e) => handleFilterChange('isActive', e.target.value)}
                        className={styles.filterSelect}
                    >
                        <option value="all">All Status</option>
                        <option value="true">Active Only</option>
                        <option value="false">Inactive Only</option>
                    </select>
                </div>

                {error && <div className={styles.error}>{error}</div>}

                {loading ? (
                    <div className={styles.loading}>Loading DNS records...</div>
                ) : records.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>No DNS records found</p>
                        <p className={styles.emptyHint}>Click "Add DNS Record" to create your first record.</p>
                        <button onClick={() => openModal()}>Create First Record</button>
                    </div>
                ) : (
                    <>
                        {/* List View (Table) */}
                        {viewMode === 'list' && (
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Domain</th>
                                            <th>Type</th>
                                            <th>Status</th>
                                            <th>Created</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {records.map((record) => {
                                            return (
                                                <tr key={record._id}>
                                                    <td>
                                                        <strong>{record.domain}</strong>
                                                    </td>
                                                    <td>
                                                        <span className={styles.typeBadge}>
                                                            {record.isLocal ? 'Local' : 'External'}
                                                        </span>
                                                        {record.isBangladesh && (
                                                            <span className={styles.typeBadge} style={{ marginLeft: 4, background: '#006a4e', color: '#fff' }}>
                                                                BD
                                                            </span>
                                                        )}
                                                        {record.excludedIps?.length > 0 && (
                                                            <span
                                                                className={styles.skipBadge}
                                                                title={`Skipped: ${record.excludedIps.join(', ')}`}
                                                            >
                                                                Skips {record.excludedIps.length}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={record.isActive ? styles.activeBadge : styles.inactiveBadge}>
                                                            {record.isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td>{new Date(record.createdAt).toLocaleDateString()}</td>
                                                    <td>
                                                        <div className={styles.tableActions}>
                                                            <button
                                                                className={styles.editButton}
                                                                onClick={() => openModal(record)}
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                className={styles.deleteButton}
                                                                onClick={() => handleDelete(record._id, record.domain)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Grid View (Cards) */}
                        {viewMode === 'grid' && (
                            <div className={styles.configGrid}>
                                {records.map((record) => {
                                    const recordCounts = getRecordTypeCounts(record.records);
                                    return (
                                        <div key={record._id} className={styles.configCard}>
                                            <div className={styles.configHeader}>
                                                <div>
                                                    <h3>{record.domain}</h3>
                                                    <p className={styles.domainMeta}>
                                                        User ID: {record.uid} • {record.isLocal ? 'Local Domain' : 'External Domain'}{record.isBangladesh ? ' • BD' : ''}
                                                    </p>
                                                    {record.excludedIps?.length > 0 && (
                                                        <p className={styles.domainMeta}>
                                                            Skipped IPs: {record.excludedIps.join(', ')}
                                                        </p>
                                                    )}
                                                </div>
                                                <span className={record.isActive ? styles.activeBadge : styles.inactiveBadge}>
                                                    {record.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>

                                            <div className={styles.configDetails}>
                                                <div className={styles.detailSection}>
                                                    <strong>DNS Records Overview</strong>
                                                    <div className={styles.recordBadges}>
                                                        {Object.entries(recordCounts).map(([type, count]) => (
                                                            <span key={type} className={styles.recordBadge}>
                                                                {type} ({count})
                                                            </span>
                                                        ))}
                                                        {Object.keys(recordCounts).length === 0 && (
                                                            <span className={styles.noRecords}>No records configured</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className={styles.detailRow}>
                                                    <strong>Created:</strong>
                                                    <span>{new Date(record.createdAt).toLocaleDateString()}</span>
                                                </div>
                                                <div className={styles.detailRow}>
                                                    <strong>Last Updated:</strong>
                                                    <span>{new Date(record.updatedAt).toLocaleDateString()}</span>
                                                </div>
                                            </div>

                                            <div className={styles.configActions}>
                                                <button
                                                    className={styles.editButton}
                                                    onClick={() => openModal(record)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className={styles.deleteButton}
                                                    onClick={() => handleDelete(record._id, record.domain)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className={styles.pagination}>
                                <button
                                    disabled={!pagination.hasPrevPage}
                                    onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                                >
                                    Previous
                                </button>
                                <span>
                                    Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalRecords} records)
                                </span>
                                <button
                                    disabled={!pagination.hasNextPage}
                                    onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* Create/Edit Modal */}
                {showModal && (
                    <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && closeModal()}>
                        <div className={styles.modal}>
                            <div className={styles.modalHeader}>
                                <h2>{editingRecord ? 'Edit DNS Record' : 'Create DNS Record'}</h2>
                                <button className={styles.closeButton} onClick={closeModal}>×</button>
                            </div>

                            <div className={styles.tabs}>
                                <button
                                    className={activeTab === 'basic' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('basic')}
                                >
                                    Basic Info
                                </button>
                                <button
                                    className={activeTab === 'a' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('a')}
                                >
                                    A Records
                                </button>
                                <button
                                    className={activeTab === 'ns' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('ns')}
                                >
                                    NS Records
                                </button>
                                <button
                                    className={activeTab === 'mx' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('mx')}
                                >
                                    MX Records
                                </button>
                                <button
                                    className={activeTab === 'txt' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('txt')}
                                >
                                    TXT / Email
                                </button>
                                <button
                                    className={activeTab === 'ssl' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('ssl')}
                                >
                                    SSL
                                </button>
                                <button
                                    className={activeTab === 'other' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('other')}
                                >
                                    Other
                                </button>
                            </div>

                            <form style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                                {activeTab === 'basic' && renderBasicTab()}
                                {activeTab === 'a' && renderARecordsTab()}
                                {activeTab === 'ns' && renderNSRecordsTab()}
                                {activeTab === 'mx' && renderMXRecordsTab()}
                                {activeTab === 'txt' && renderTXTRecordsTab()}
                                {activeTab === 'ssl' && renderSSLTab()}
                                {activeTab === 'other' && renderOtherRecordsTab()}
                            </form>

                            <div className={styles.modalFooter}>
                                <button
                                    type="button"
                                    className={styles.cancelButton}
                                    onClick={closeModal}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className={styles.submitButton}
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                >
                                    {submitting ? 'Saving...' : (editingRecord ? 'Update Record' : 'Create Record')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default DNSRecords;
